import { action, makeObservable, observable, override, runInAction } from 'mobx';

import { buildWhereFilter, get, getCount, getPage, post } from 'stores/_services/dataService';
import { getConditions } from 'stores/_services/filterService';
import AgentStore from 'stores/agent/AgentStore';
import BaseListStore, { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from '../BaseListStore';
import { SortDirection } from 'constants/SortDirection';

const SEARCH_FIELDS = ['name', 'hostname', 'os', 'ipstring', 'token'];
const DEFAULT_FILTER = {
    search: '',
    authorized: {
        authorized: false,
        pending: false,
        disabled: false
    }
};

export interface IAgentRmmData {
    active: boolean;
    platform: string;
    deviceShortId: string;
    rmmActive: boolean;
}

export default class AgentListStore extends BaseListStore<AgentStore> {
    filteredCount = 0;

    constructor() {
        super({
            itemStore: AgentStore,
            sortBy: 'heartbeat',
            sortDirection: SortDirection.DESC,
            defaultFilter: DEFAULT_FILTER
        });

        makeObservable(this, {
            resetFilter: override,
            clearIds: override,
            getTotalCount: action,
            getAgent: action,
            getAgents: action,
            installAgents: action,
            uninstallAgents: action,
            uninstallAgent: action,
            assignAgentsToTargetGroup: action,
            filteredCount: observable
        });
    }

    get agents(): AgentStore[] {
        return this.ids.map((id) => this.data[id]);
    }

    resetFilter(): void {
        this.filter = { ...DEFAULT_FILTER };
    }

    setFilter(filterItems: any) {
        this.filter = {
            ...this.filter,
            ...filterItems
        };
    }

    clearIds(): void {
        this.ids = [];
    }

    async getTotalCount(): Promise<void> {
        const totalCount = await getCount('agents', { searchFields: SEARCH_FIELDS });
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.totalCount = totalCount;
        });
    }

    async getAgent(agentId: string): Promise<AgentStore> {
        const { data } = await get(`agentdetails/${agentId}`);

        this._updateData(data);
        return this.data[agentId];
    }

    async getAgents(args = {}, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        const shouldReset = this._updateListState(args);

        const where = getConditions(this.filter);
        const searchFields = SEARCH_FIELDS;

        const options = {
            filter: {
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                searchFields,
                where,
                order: this.sort
            }
        };
        const { data } = await getPage('agentdetails', options);
        const totalCount = await getCount('agentdetails', { where, searchFields });

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, 0);
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });
    }

    async installAgents(conditions: any) {
        const where = buildWhereFilter({
            where: conditions,
            searchFields: SEARCH_FIELDS
        });

        await post('agents/install', { where });

        this.selectedIds = {};
        await this.getAgents();
    }

    async uninstallAgents(conditions: Record<string, unknown>): Promise<void> {
        const where = buildWhereFilter({
            where: conditions,
            searchFields: SEARCH_FIELDS
        });

        await post('agents/uninstall', { where });

        this.selectedIds = {};
        await this.getAgents();
    }

    async uninstallAgent(agentId: string): Promise<void> {
        await post(`agents/${agentId}/uninstall`);
        this.selectedIds = {};
        await this.getAgents();
    }

    async getAgentPolicies(agentId: string): Promise<[]> {
        const { data } = await get(`agents/${agentId}/getAgentPolicies`);
        return data;
    }

    async assignAgentsToTargetGroup(targetId: string, conditions: Record<string, unknown>): Promise<void> {
        const filter: any = conditions ? { where: buildWhereFilter({ where: conditions, searchFields: SEARCH_FIELDS }) } : undefined;

        await post('agents/assignTarget', { targetId, filter });

        this.selectedIds = {};
        await this.getAgents();
    }

    public async getAgentRmmData(agentId: string): Promise<IAgentRmmData> {
        if (!agentId) {
            return {} as any;
        }

        try {
            const { data } = await get(`agents/${agentId}/isActive`);
            return data;
        } catch (err) {
            return {} as any;
        }
    }
}
